import React, { useState } from 'react';
import { 
  BookOpen, 
  Package, 
  ShoppingCart, 
  Zap, 
  Search, 
  Image as ImageIcon, 
  Shield, 
  Database, 
  Mail, 
  Star,
  TrendingUp,
  CheckCircle,
  DollarSign,
  Globe
} from 'lucide-react';
import { t } from '../services/i18n';

interface SEOResourcesProps {
  currentUser: any;
}

type Platform = 'wordpress' | 'shopify';
type Category = 'speed' | 'seo' | 'images' | 'security' | 'backup' | 'forms' | 'email' | 'reviews' | 'conversion' | 'utilities';

interface Plugin {
  name: string;
  type: 'free' | 'paid' | 'freemium';
  price?: string;
  description: string;
  recommended?: boolean;
  link?: string;
}

const wordpressPlugins: Record<Category, Plugin[]> = {
  speed: [
    { name: 'WP Rocket', type: 'paid', price: '$59-$299/year', description: 'BEST OVERALL - One-click setup, handles caching, lazy loading, minification, database optimization. Most beginner-friendly.', recommended: true },
    { name: 'NitroPack', type: 'paid', price: '$21-$176/month', description: 'Complete optimization platform with CDN included. Set it and forget it.' },
    { name: 'Perfmatters', type: 'paid', price: '$5.75-$9.58/month', description: 'Works alongside caching plugins to disable unnecessary scripts. Pairs perfectly with WP Rocket.' },
    { name: 'W3 Total Cache', type: 'free', description: 'Advanced but complex. Great if you know what you\'re doing.' },
    { name: 'WP Fastest Cache', type: 'free', description: 'Simpler than W3, good middle ground.' },
    { name: 'Autoptimize', type: 'free', description: 'Handles minification and optimization. Use with a caching plugin.' },
    { name: 'WP-Optimize', type: 'free', description: 'Database cleanup + caching. Good all-in-one free option.' },
    { name: 'LiteSpeed Cache', type: 'free', description: 'Only if your host uses LiteSpeed servers. Powerful but host-specific.' },
  ],
  seo: [
    { name: 'Rank Math', type: 'free', description: 'RECOMMENDED - Most features, doesn\'t slow your site, great free version.', recommended: true },
    { name: 'Yoast SEO', type: 'free', description: 'Most popular, very beginner-friendly, lots of tutorials available.' },
    { name: 'The SEO Framework', type: 'free', description: 'Lightweight, less bloated than Yoast.' },
    { name: 'All in One SEO', type: 'free', description: 'Good Yoast alternative with modular system.' },
    { name: 'Rank Math Pro', type: 'paid', price: '$59+/year', description: 'Advanced schema, analytics, keyword tracking.' },
  ],
  images: [
    { name: 'Smush', type: 'free', description: 'Best free option, unlimited compressions on free plan.', recommended: true },
    { name: 'ShortPixel', type: 'freemium', description: '100 free images/month, then paid.' },
    { name: 'EWWW Image Optimizer', type: 'free', description: 'Solid free option.' },
    { name: 'Imagify', type: 'paid', price: '$4.99+/month', description: 'From WP Rocket team, excellent compression.' },
    { name: 'ShortPixel Unlimited', type: 'paid', price: '$9.99+/month', description: 'Unlimited optimization.' },
  ],
  security: [
    { name: 'Wordfence Security', type: 'free', description: 'MOST POPULAR - Firewall, malware scanner, login security. Can slow sites on shared hosting.', recommended: true },
    { name: 'All In One WP Security & Firewall', type: 'free', description: 'Beginner-friendly with grading system.' },
    { name: 'Solid Security (formerly iThemes)', type: 'free', description: 'Protects against 30+ attack types.' },
    { name: 'Sucuri Security', type: 'paid', price: '$199.99+/year', description: 'Website firewall, DDoS protection, malware removal. Professional level.' },
    { name: 'MalCare Security', type: 'paid', price: '$99+/year', description: 'Best malware scanner, one-click cleanup, doesn\'t slow your site.' },
    { name: 'Wordfence Premium', type: 'paid', price: '$119+/year', description: 'Real-time updates, country blocking, advanced features.' },
  ],
  backup: [
    { name: 'UpdraftPlus', type: 'free', description: 'Best free backup plugin, stores to Google Drive/Dropbox.', recommended: true },
    { name: 'BackWPup', type: 'free', description: 'Solid alternative.' },
    { name: 'UpdraftPlus Premium', type: 'paid', price: '$70/year', description: 'More storage options, migrations, priority support.' },
    { name: 'BackupBuddy', type: 'paid', price: '$80+/year', description: 'Complete backup + migration tool.' },
  ],
  forms: [
    { name: 'WPForms Lite', type: 'free', description: 'Drag-and-drop form builder.', recommended: true },
    { name: 'Contact Form 7', type: 'free', description: 'Most popular but requires more setup.' },
    { name: 'Formidable Forms', type: 'free', description: 'Advanced features in free version.' },
    { name: 'WPForms Pro', type: 'paid', price: '$49.50+/year', description: 'Payment integration, advanced fields.' },
  ],
  email: [],
  reviews: [],
  conversion: [],
  utilities: [
    { name: 'Redirection', type: 'free', description: 'Manage 301 redirects and track 404 errors.' },
    { name: 'WP Mail SMTP', type: 'free', description: 'Fix email delivery issues.' },
    { name: 'Enable Media Replace', type: 'free', description: 'Replace images without breaking links.' },
    { name: 'Duplicate Page', type: 'free', description: 'Clone posts/pages quickly.' },
  ],
};

const shopifyApps: Record<Category, Plugin[]> = {
  speed: [
    { name: 'Hyperspeed EXTREME', type: 'paid', price: '$9.99-$34.99/month', description: 'Minification, lazy load, critical CSS, app optimization.' },
    { name: 'TinyIMG', type: 'paid', price: '$14-$59/month', description: 'Image compression + SEO + speed optimization. RECOMMENDED FOR BEGINNERS', recommended: true },
    { name: 'Avada SEO Suite', type: 'paid', price: '$34.95/month', description: 'All-in-one: speed + SEO + image optimization.' },
    { name: 'SEOAnt', type: 'paid', price: '$9.99-$49.99/month', description: 'One-click speed optimization with different modes.' },
    { name: 'Tapita SEO & Speed', type: 'paid', price: '$19.99-$99/month', description: 'Instant page preload, real user monitoring.' },
    { name: 'Booster: Page Speed Optimizer', type: 'free', description: 'Free plan available, instant optimization.' },
    { name: 'SearchPie', type: 'free', description: 'Free plan with instant page feature.' },
  ],
  seo: [
    { name: 'Booster SEO & Image Optimizer', type: 'freemium', price: 'Free + $29.99/month Pro', description: 'Autopilot SEO, fixes broken links automatically. MOST POPULAR', recommended: true },
    { name: 'Plug In SEO', type: 'paid', price: '$29.99/month', description: 'SEO checker with detailed fixes.' },
    { name: 'Smart SEO', type: 'paid', price: '$9.99-$19.99/month', description: 'AI meta tags, multilingual support.' },
    { name: 'Avada SEO Suite', type: 'paid', price: '$34.95/month', description: 'Comprehensive SEO toolkit.' },
    { name: 'JSON-LD for SEO', type: 'paid', price: 'From $14.99/month', description: 'Structured data for large stores.' },
  ],
  images: [
    { name: 'TinyIMG', type: 'paid', price: '$14-$59/month', description: 'BEST OVERALL - Auto-compression, lazy loading, ALT text optimization.', recommended: true },
    { name: 'Crush Pics', type: 'paid', price: '$7-$99/month', description: 'Set-and-forget image compression.' },
    { name: 'Image Optimizer by Booster', type: 'free', description: 'Included in Booster SEO suite.' },
  ],
  security: [
    { name: 'Cloudflare', type: 'free', description: 'External - Free CDN + DDoS protection + firewall.', recommended: true },
    { name: 'Rewind Backups', type: 'paid', price: '$3-$99/month', description: 'Automatic daily backups of your entire store.' },
  ],
  backup: [
    { name: 'Rewind Backups', type: 'paid', price: '$3-$99/month', description: 'Automatic daily backups of your entire store.', recommended: true },
  ],
  forms: [],
  email: [
    { name: 'Klaviyo', type: 'freemium', description: 'Free up to 250 contacts, then paid. Best email marketing for Shopify.', recommended: true },
    { name: 'Omnisend', type: 'freemium', description: 'Free up to 250 contacts. Email + SMS marketing.' },
  ],
  reviews: [
    { name: 'Judge.me', type: 'freemium', price: 'Free + $15/month Pro', description: 'Product reviews with photos.', recommended: true },
    { name: 'Fera', type: 'paid', price: '$9-$199/month', description: 'All-in-one social proof platform.' },
    { name: 'Loox', type: 'paid', price: '$9.99-$299.99/month', description: 'Photo reviews + referrals.' },
  ],
  conversion: [
    { name: 'Privy', type: 'freemium', price: 'Free + $15+/month', description: 'Pop-ups, email capture, exit intent.', recommended: true },
    { name: 'OptinMonster', type: 'paid', price: '$9+/month', description: 'Advanced pop-ups and lead generation.' },
    { name: 'Frequently Bought Together', type: 'paid', price: '$9.99-$49.99/month', description: 'Product recommendations.' },
  ],
  utilities: [
    { name: 'Google & YouTube', type: 'free', description: 'Google Analytics + Google Ads integration.' },
    { name: 'Facebook & Instagram', type: 'free', description: 'Connect social selling channels.' },
    { name: 'Reconvert', type: 'paid', price: '$7.99-$79.99/month', description: 'Upsells on thank you page.' },
    { name: 'Bold Subscriptions', type: 'paid', price: '$49.99/month', description: 'Recurring payments.' },
  ],
};

const categoryIcons: Record<Category, React.ReactNode> = {
  speed: <Zap className="w-5 h-5" />,
  seo: <Search className="w-5 h-5" />,
  images: <ImageIcon className="w-5 h-5" />,
  security: <Shield className="w-5 h-5" />,
  backup: <Database className="w-5 h-5" />,
  forms: <Mail className="w-5 h-5" />,
  email: <Mail className="w-5 h-5" />,
  reviews: <Star className="w-5 h-5" />,
  conversion: <TrendingUp className="w-5 h-5" />,
  utilities: <Package className="w-5 h-5" />,
};

// Safe translation helper: avoids showing raw keys like "seoResources.xxx" if a translation is missing
const translate = (key: string, fallback: string): string => {
  const value = t(key);
  if (!value || value === key) {
    return fallback;
  }
  return value;
};

const getCategoryLabel = (category: Category): string => {
  const labels: Record<Category, string> = {
    speed: translate('seoResources.speedOptimization', 'Speed Optimization'),
    seo: translate('seoResources.seo', 'SEO'),
    images: translate('seoResources.imageOptimization', 'Image Optimization'),
    security: translate('seoResources.security', 'Security'),
    backup: translate('seoResources.backup', 'Backup'),
    forms: translate('seoResources.forms', 'Forms'),
    email: translate('seoResources.emailMarketing', 'Email Marketing'),
    reviews: translate('seoResources.reviews', 'Reviews'),
    conversion: translate('seoResources.conversionOptimization', 'Conversion Optimization'),
    utilities: translate('seoResources.essentialUtilities', 'Essential Utilities'),
  };
  return labels[category];
};

// Helper function to get translated plugin description
const getPluginDescription = (pluginName: string, defaultDesc: string): string => {
  // Map plugin names to translation keys
  const nameMap: Record<string, string> = {
    'WP Rocket': 'WPRocket',
    'NitroPack': 'NitroPack',
    'Perfmatters': 'Perfmatters',
    'W3 Total Cache': 'W3TotalCache',
    'WP Fastest Cache': 'WPFastestCache',
    'Autoptimize': 'Autoptimize',
    'WP-Optimize': 'WP-Optimize',
    'LiteSpeed Cache': 'LiteSpeedCache',
    'Rank Math': 'RankMath',
    'Yoast SEO': 'YoastSEO',
    'The SEO Framework': 'TheSEOFramework',
    'All in One SEO': 'AllinOneSEO',
    'Rank Math Pro': 'RankMathPro',
    'Smush': 'Smush',
    'ShortPixel': 'ShortPixel',
    'EWWW Image Optimizer': 'EWWWImageOptimizer',
    'Imagify': 'Imagify',
    'ShortPixel Unlimited': 'ShortPixelUnlimited',
    'Wordfence Security': 'WordfenceSecurity',
    'All In One WP Security & Firewall': 'AllInOneWPSecurityFirewall',
    'Solid Security (formerly iThemes)': 'SolidSecurityformerlyiThemes',
    'Sucuri Security': 'SucuriSecurity',
    'MalCare Security': 'MalCareSecurity',
    'Wordfence Premium': 'WordfencePremium',
    'UpdraftPlus': 'UpdraftPlus',
    'BackWPup': 'BackWPup',
    'UpdraftPlus Premium': 'UpdraftPlusPremium',
    'BackupBuddy': 'BackupBuddy',
    'WPForms Lite': 'WPFormsLite',
    'Contact Form 7': 'ContactForm7',
    'Formidable Forms': 'FormidableForms',
    'WPForms Pro': 'WPFormsPro',
    'Redirection': 'Redirection',
    'WP Mail SMTP': 'WPMailSMTP',
    'Enable Media Replace': 'EnableMediaReplace',
    'Duplicate Page': 'DuplicatePage',
    'Hyperspeed EXTREME': 'HyperspeedEXTREME',
    'TinyIMG': 'TinyIMG',
    'Avada SEO Suite': 'AvadaSEOSuite',
    'SEOAnt': 'SEOAnt',
    'Tapita SEO & Speed': 'TapitaSEOSpeed',
    'Booster: Page Speed Optimizer': 'BoosterPageSpeedOptimizer',
    'SearchPie': 'SearchPie',
    'Booster SEO & Image Optimizer': 'BoosterSEOImageOptimizer',
    'Plug In SEO': 'PlugInSEO',
    'Smart SEO': 'SmartSEO',
    'JSON-LD for SEO': 'JSON-LDforSEO',
    'Crush Pics': 'CrushPics',
    'Image Optimizer by Booster': 'ImageOptimizerbyBooster',
    'Cloudflare': 'Cloudflare',
    'Rewind Backups': 'RewindBackups',
    'Klaviyo': 'Klaviyo',
    'Omnisend': 'Omnisend',
    'Judge.me': 'Judgeme',
    'Fera': 'Fera',
    'Loox': 'Loox',
    'Privy': 'Privy',
    'OptinMonster': 'OptinMonster',
    'Frequently Bought Together': 'FrequentlyBoughtTogether',
    'Google & YouTube': 'GoogleYouTube',
    'Facebook & Instagram': 'FacebookInstagram',
    'Reconvert': 'Reconvert',
    'Bold Subscriptions': 'BoldSubscriptions',
  };
  
  const key = nameMap[pluginName] || pluginName.replace(/\s+/g, '').replace(/[^a-zA-Z0-9]/g, '');
  const value = t(`seoResources.plugins.${key}.description`);
  if (!value || value === `seoResources.plugins.${key}.description`) {
    return defaultDesc;
  }
  return value;
};

export const SEOResources: React.FC<SEOResourcesProps> = ({ currentUser }) => {
  const [platform, setPlatform] = useState<Platform>('wordpress');
  const [selectedCategory, setSelectedCategory] = useState<Category>('speed');

  const plugins = platform === 'wordpress' ? wordpressPlugins : shopifyApps;
  const currentPlugins = plugins[selectedCategory] || [];

  const getTypeBadge = (type: string) => {
    const colors = {
      free: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50',
      paid: 'bg-amber-500/20 text-amber-300 border-amber-500/50',
      freemium: 'bg-blue-500/20 text-blue-300 border-blue-500/50',
    };
    return colors[type as keyof typeof colors] || 'bg-slate-500/20 text-slate-300 border-slate-500/50';
  };

  return (
    <div className="min-h-screen bg-[#0a0e27] text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-3 rounded-xl">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">{translate('seoResources.title', 'SEO Resources & Tools')}</h1>
              <p className="text-slate-400">{translate('seoResources.subtitle', 'Essential plugins and apps for WordPress & Shopify')}</p>
            </div>
          </div>
        </div>

        {/* Platform Selector */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 mb-6">
          <div className="flex gap-4">
            <button
              onClick={() => setPlatform('wordpress')}
              className={`flex-1 flex items-center justify-center gap-3 px-6 py-4 rounded-xl font-semibold transition-all ${
                platform === 'wordpress'
                  ? 'bg-indigo-600 text-white shadow-lg'
                  : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
              }`}
            >
              <Globe className="w-5 h-5" />
              {translate('seoResources.wordpress', 'WordPress')}
            </button>
            <button
              onClick={() => setPlatform('shopify')}
              className={`flex-1 flex items-center justify-center gap-3 px-6 py-4 rounded-xl font-semibold transition-all ${
                platform === 'shopify'
                  ? 'bg-indigo-600 text-white shadow-lg'
                  : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
              }`}
            >
              <ShoppingCart className="w-5 h-5" />
              {translate('seoResources.shopify', 'Shopify')}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Categories Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 sticky top-6">
              <h3 className="text-lg font-bold text-white mb-4">{translate('seoResources.categories', 'Categories')}</h3>
              <div className="space-y-2">
                {(['speed', 'seo', 'images', 'security', 'backup', 'forms', 'email', 'reviews', 'conversion', 'utilities'] as Category[]).map((category) => {
                  const hasPlugins = (plugins[category] || []).length > 0;
                  if (!hasPlugins) return null;
                  
                  return (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${
                        selectedCategory === category
                          ? 'bg-indigo-600 text-white'
                          : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      {categoryIcons[category]}
                      <span className="text-sm font-medium">{getCategoryLabel(category)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Plugins List */}
          <div className="lg:col-span-3">
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">{getCategoryLabel(selectedCategory)}</h2>
                <span className="text-slate-400 text-sm">
                  {currentPlugins.length}{' '}
                  {currentPlugins.length === 1
                    ? translate('seoResources.option', 'option')
                    : translate('seoResources.options', 'options')}
                </span>
              </div>

              <div className="space-y-4">
                {currentPlugins.map((plugin, idx) => (
                  <div
                    key={idx}
                    className="bg-slate-900/50 border border-slate-700 rounded-xl p-5 hover:border-indigo-500/50 transition-all"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-bold text-white">{plugin.name}</h3>
                        {plugin.recommended && (
                          <span className="bg-emerald-500/20 text-emerald-300 px-2 py-1 rounded-md text-xs font-semibold border border-emerald-500/50">
                            {translate('seoResources.recommended', 'Recommended')}
                          </span>
                        )}
                      </div>
                      <span className={`px-3 py-1 rounded-md text-xs font-semibold border ${getTypeBadge(plugin.type)}`}>
                        {plugin.type === 'freemium'
                          ? translate('seoResources.freemium', 'Free + Paid')
                          : plugin.type === 'free'
                          ? translate('seoResources.free', 'Free')
                          : translate('seoResources.paid', 'Paid')}
                      </span>
                    </div>
                    
                    {plugin.price && (
                      <div className="flex items-center gap-2 mb-2 text-amber-400">
                        <DollarSign className="w-4 h-4" />
                        <span className="text-sm font-semibold">{plugin.price}</span>
                      </div>
                    )}
                    
                    <p className="text-slate-300 text-sm leading-relaxed">
                      {getPluginDescription(plugin.name, plugin.description)}
                    </p>
                  </div>
                ))}
              </div>

              {currentPlugins.length === 0 && (
                <div className="text-center py-12">
                  <Package className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                  <p className="text-slate-400">
                    {translate('seoResources.noPlugins', `No plugins/apps available in this category for ${platform}.`)}
                  </p>
                </div>
              )}
            </div>

            {/* Recommended Stack */}
            <div className="mt-6 bg-gradient-to-br from-indigo-600/20 to-purple-600/20 border border-indigo-500/50 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle className="w-5 h-5 text-indigo-400" />
                <h3 className="text-xl font-bold text-white">
                  {translate('seoResources.recommendedStack', 'Recommended Stack for Beginners')}
                </h3>
              </div>
              
              {platform === 'wordpress' ? (
                <div className="space-y-3">
                  <p className="text-slate-300 mb-4">
                    {translate('seoResources.recommendedStackDesc', 'Start with these essential plugins:')}
                  </p>
                  <ul className="space-y-2 text-slate-300">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span>
                        <strong>WP Rocket</strong> - {translate('seoResources.stackWPRocket', 'Speed optimization')}
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span>
                        <strong>Rank Math</strong> - {translate('seoResources.stackRankMath', 'SEO')}
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span>
                        <strong>Smush</strong> - {translate('seoResources.stackSmush', 'Image optimization')}
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span>
                        <strong>Wordfence</strong> - {translate('seoResources.stackWordfence', 'Security')}
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span>
                        <strong>UpdraftPlus</strong> - {translate('seoResources.stackUpdraftPlus', 'Backups')}
                      </span>
                    </li>
                  </ul>
                  <p className="text-sm text-slate-400 mt_4">
                    {translate('seoResources.totalCost', 'Total cost: ~$200-300/year for premium plugins')}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-slate-300 mb-4">
                    {translate('seoResources.stackShopifyDesc', 'Start with these essential apps:')}
                  </p>
                  <ul className="space-y-2 text-slate-300">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span>
                        <strong>TinyIMG</strong> - {translate('seoResources.stackTinyIMG', 'Speed + SEO + Images')}
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span>
                        <strong>Booster SEO</strong> - {translate('seoResources.stackBoosterSEO', 'Automated SEO')}
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span>
                        <strong>Klaviyo or Omnisend</strong> - {translate('seoResources.stackEmail', 'Email marketing')}
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span>
                        <strong>Judge.me</strong> - {translate('seoResources.stackReviews', 'Reviews')}
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span>
                        <strong>Rewind</strong> - {translate('seoResources.stackBackups', 'Backups')}
                      </span>
                    </li>
                  </ul>
                  <p className="text-sm text-slate-400 mt-4">
                    {translate('seoResources.totalCostShopify', 'Total cost: ~$100-150/month')}
                  </p>
                </div>
              )}
              
              <div className="mt-4 p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                <p className="text-sm text-slate-300">
                  <strong className="text-amber-400">
                    {translate('seoResources.proTip', 'Pro tip:')}
                  </strong>{' '}
                  {translate(
                    'seoResources.proTipDesc',
                    "Don't install everything at once. Start with speed + SEO + security, test your site, then add more as needed. Too many plugins = slower site = defeats the purpose."
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

